import debounce from 'debounce';
import { ANNOTATION_TARGETS, WRAPPER_TYPES, MAX_STATE_UPDATES_PER_SECOND } from 'constants';
import { LEGACY_ENVIRONMENTS } from 'configuration/environment';
import { mergeUnique } from 'utilities/data';
import { minimumResolution } from 'utilities/time';
import { getStateLogger } from 'utilities/state';
import { supportedVersion, VERSION_SUPPORTED } from 'utilities/version';
import { RELOAD_TYPES } from 'constants';
import { APPLICATION_EVENT, ANNOTATION_EVENT, VIEW_PAGE } from 'constants/events';
import { StateEngine } from 'engine/state/state_engine';
import {
    migrate,
    parseOverrides,
    distillAndApplyMetadata
} from 'utilities/parsers';

import {
    modifyClasses,
    generateAnnotationConfig,
    mergeAnnotations,
    generateVariation,
    generateLanguageVariation
} from 'utilities/annotations';
import { adjacentShowAnnotationOverride } from './utilities/data';
import { addResponseDataToQuizAnnotations } from './utilities/data_loader_utilities/addResponseDataToQuizAnnotations';
import { addPresentModePropToAnswerAnnotation } from './utilities/data_loader_utilities/addPresentModePropToAnswerAnnotation';

export class DataLoader {
    constructor(options, eventBus) {
        this.options = { ...options, overrides: migrate(options.overrides) };
        this.lastLocalData = { ...this.options.overrides };
        this.eventBus = eventBus;
        this.stateEngine = new StateEngine({ eventBus, config: this.lastLocalData });
        this.eventBus.on(ANNOTATION_EVENT.ADD, this.onAnnotationAdded);
        this.eventBus.on(ANNOTATION_EVENT.REMOVE, this.onAnnotationRemoved);
        this.eventBus.on(ANNOTATION_EVENT.MODIFY, this.onAnnotationModified);
        this.eventBus.on(ANNOTATION_EVENT.SHOW, this.onAnnotationShow);
        this.eventBus.on(ANNOTATION_EVENT.HIDE, this.onAnnotationHide);
        this.eventBus.on(ANNOTATION_EVENT.TOGGLE, this.onAnnotationToggle);
        this.eventBus.on(ANNOTATION_EVENT.LANGUAGE_LOAD, this.onLanguageLoad);
        this.eventBus.on(APPLICATION_EVENT.STATE_CHANGE, this.onStateChange);
        this.eventBus.on(ANNOTATION_EVENT.ADD_CLASS, this.onAddClass);
        this.eventBus.on(ANNOTATION_EVENT.REMOVE_CLASS, this.onRemoveClass);
        this.eventBus.on(ANNOTATION_EVENT.SET_CLASS, this.onSetClass);
        this.eventBus.on(ANNOTATION_EVENT.CONDITIONAL_SETTINGS_LOADED, this.onConditionalSettingsLoad);
        this.eventBus.on(VIEW_PAGE.QUIZ_RESPONSE_DATA_LOADED, this.onQuizDataLoaded)
        this.eventBus.on(VIEW_PAGE.PRESENT_MODE_ACTIVE, (payload) => this.onPresentModeActive(payload))

        this.eventBus.on(VIEW_PAGE.PRESENT_MODE_ACTIVE, (payload) => this.toggleQuizAndPhantomsVisibility(payload))

        this.environment = LEGACY_ENVIRONMENTS[options.environment];
        this.runtimeAnnotations = [generateAnnotationConfig('mute')];
        this.runtimeAnnotationModifications = {};
    }

    toggleQuizAndPhantomsVisibility = (payload) => {
        const {annotations} = this.lastLocalData
            
            this.lastLocalData = {
                ...this.lastLocalData,
                annotations: annotations.map((annotation) => {
                    if (annotation.type === 'quiz' || annotation.type === 'phantom') {
                        return {
                            ...annotation,
                            mode: payload.isPresentModeActiveWithoutQuiz ? 'invisible' : undefined
                        }
                    }
                    return annotation;
                })
            }
    
            this.loadAndBroadcast();
    }

    // Listen and handle adding a present mode prop to quiz answer annotation.
    onPresentModeActive = (payload) => {
        const {annotations} = this.lastLocalData
        
        const newAnnotations = addPresentModePropToAnswerAnnotation(payload, annotations)
        this.lastLocalData = {
            ...this.lastLocalData,
            annotations: newAnnotations
        }

        this.loadAndBroadcast();
    }
    
    // Listen and handle quiz response data event 
    onQuizDataLoaded = (quizResponseData) => {
        const {annotations} = this.lastLocalData

        const annotationsWithQuizResponseData = addResponseDataToQuizAnnotations(quizResponseData, annotations)
        this.lastLocalData = {
            ...this.lastLocalData,
            annotations: annotationsWithQuizResponseData
        }

        this.loadAndBroadcast();
    }
    
    onAnnotationAdded = annotationConfig => {
        this.runtimeAnnotations = this.runtimeAnnotations.filter(ra => ra.id !== annotationConfig.id)
        this.runtimeAnnotations.push(annotationConfig);
        this.loadAndBroadcast();
    }

    onAnnotationRemoved = id => {
        this.runtimeAnnotations = this.runtimeAnnotations.filter(a => a.id !== id);
        this.lastLocalData = {
            ...this.lastLocalData,
            annotations: this.lastLocalData.annotations.filter(a => a.id !== id)
        }
        this.loadAndBroadcast();
    }

    onAnnotationModified = modifications => {
        if (!Array.isArray(modifications)) {
            modifications = [modifications];
        }

        // convert annotations to hash for faster match
        modifications = modifications.reduce((hash,m) => {
            hash[m.id] = m;

            // backup modifications for re-application during `reload`
            // performing inside `reduce` (gross, I know) to avoid additional iteration
            this.updateRuntimeAnnotationModifications(m.id, m);

            return hash;
        }, {});

        const { annotations, ...overrides } = this.lastLocalData;

        for (let i = 0, len = annotations.length; i < len; i++) {
            const modifier = modifications[annotations[i].id];

            if (modifier) {
                annotations[i] = mergeAnnotations(annotations[i], modifier);
            }
        }

        overrides.annotations = annotations;
        this.loadAndBroadcast(overrides);
    }

    onAddClass = ({id, value}) => {
        const toUpdate = this.lastLocalData.annotations.find(a => a.id === id)

        if (toUpdate) {
            this.onAnnotationModified({
                id, 
                classes: modifyClasses(value, toUpdate.classes, 'addClass')
            })
        }

        this.loadAndBroadcast(this.lastLocalData)
    }

    onRemoveClass = ({id, value}) => {
        const toUpdate = this.lastLocalData.annotations.find(a => a.id === id)

        if (toUpdate) {
            this.onAnnotationModified({
                id, 
                classes: modifyClasses(value, toUpdate.classes, 'removeClass')
            })
        }

        this.loadAndBroadcast(this.lastLocalData)
    }

    onSetClass = ({id, value}) => {
        const toUpdate = this.lastLocalData.annotations.find(a => a.id === id)

        if (toUpdate) {
            this.onAnnotationModified({
                id, 
                classes: value
            })
        }
        this.loadAndBroadcast(this.lastLocalData)
    }

    onAnnotationShow = (id, overrideDefault = false) => {
        const { annotations } = this.lastLocalData;
        const currentTargetedAnnotation = annotations.filter(a => a.id === id)[0] || {};
        const adjacentAnnotationsInSamePanel = annotations.filter(a => {
            return currentTargetedAnnotation.target.includes('adjacent') &&  a.target === currentTargetedAnnotation.target;
        });
        const samePanelAdjacentAnnotationIds = adjacentAnnotationsInSamePanel.map(a => a.id)
        const shouldOverride = (id) => {
            return !!adjacentAnnotationsInSamePanel.filter(annotation => {
                return annotation.id === id
            }).length
        };
        
        if (shouldOverride(id)) {
            let newAnnotations = adjacentShowAnnotationOverride(annotations, id, samePanelAdjacentAnnotationIds)

            this.lastLocalData = {
                ...this.lastLocalData,
                annotations: newAnnotations
            }
        } else {
            this.lastLocalData = {
                ...this.lastLocalData,
                annotations: this.lastLocalData.annotations.map(a => a.id === id ? { ...a, mode: undefined } : a)
            }
        }
        this.loadAndBroadcast(this.lastLocalData);
    }

    onAnnotationHide = id => {
        this.lastLocalData = {
            ...this.lastLocalData,
            annotations: this.lastLocalData.annotations.map(a => a.id === id ? { ...a, mode: 'invisible' } : a)
        }
        this.loadAndBroadcast(this.lastLocalData);
    }

    onAnnotationToggle = id => {
        const { annotations = [] } = this.lastLocalData,
            toToggle = annotations.find(a => a.id === id);

        if (toToggle) {
            this.onAnnotationModified({
                id,
                mode: toToggle.mode === 'invisible' ? undefined : 'invisible'
            })
        }
    }

    onLanguageLoad = (languageContents, language) => {
        const { annotations = [] } = this.lastLocalData;

        // assumes a single video text annotation for now. will require updates if we support multiple video text annotations
        const videoTextAnnotation = annotations.find(a => a.type === 'videotext') || {};
        const videoTextIds = videoTextAnnotation.properties && videoTextAnnotation.properties.id || [];
        const contentsForVideoText = videoTextIds.some(id => id in languageContents);
        let variated;

        if (contentsForVideoText) {
            const { id } = videoTextAnnotation;            
            variated = {
                id,
                ...generateLanguageVariation(language, {
                    properties: {
                        content: videoTextIds.map(id => languageContents[id])
                    }
                })
            }
        } else {
            variated = Object.keys(languageContents).map(id => ({
                id,
                ...generateLanguageVariation(language, { content: languageContents[id] })
            }));         
        }

        if (variated) {
            this.onAnnotationModified(variated);
        }
    }

    onConditionalSettingsLoad = (contentChanges = {}, conditions = {}) => {
        const { annotations = [] } = this.lastLocalData;

        const updates = annotations.reduce((updates, a) => {
            if (a.id in contentChanges) {
                updates.push({
                    id: a.id,
                    ...generateVariation(
                        conditions,
                        { content: contentChanges[a.id] }
                    )
                })
            } else if (
                a.nestedContent &&
                Array.isArray(a.properties?.id) &&
                a.properties.id.some(id => id in contentChanges)
            ) {
                updates.push({
                    id: a.id,
                    ...generateVariation(
                        conditions,
                        {
                            properties: {
                                content: a.properties.id.map(id => contentChanges[id])
                            }
                        }
                    )
                })
            }

            return updates;
        }, []);

        if (updates.length) {
            this.onAnnotationModified(updates);
        }
    }

    // Sometimes state updates may occur too quickly (Quiz Data update, then Annotation Update immediately after)
    // which can cause unpredictability during rendering and strange bugs, particularly with external panels.
    // Since `onStateChange` is really just merging the most recent config with the state of "watchable" states,
    // skipping a few updates using debounce should not have any ill effects since the state of "watchable" states accumulates changes    
    onStateChange = debounce(({emitter}) => {
        const updates = {
            ...this.loadSetup(this.lastLocalData),
            emitter
        }
        
        return this.eventBus.emit(APPLICATION_EVENT.UPDATED, updates);
    }, 1000 / MAX_STATE_UPDATES_PER_SECOND);

    reload(overrides, reloadType = RELOAD_TYPES.DATA) {
        let executeLoad;

        overrides = migrate(overrides);

        if (this.options.overrides) {
            const optionOverrides = { ...this.options.overrides };

            // new annotation overrides should completely replace annotations specified in options
            delete optionOverrides.annotations;

            overrides = mergeUnique(optionOverrides, overrides);
        }

        overrides.annotations = this.applyRuntimeAnnotationModifications(overrides.annotations);

        return this.loadAndBroadcast(overrides);
    }

    initialize() {
        return this.loadSetup(this.options.overrides);
    }

    loadAndBroadcast(overrides) { 
        return this.eventBus.emit(APPLICATION_EVENT.UPDATED, this.loadSetup(overrides));
    }

    loadSetup(overrides) {
        if (!this.checkVersion(this.options.version) && !this.checkVersion(overrides.version)) {
            throw("Unsupported Version");
        }

        this.lastLocalData = { ...this.lastLocalData, ...overrides };
        return this.reconcileSetup(this.loadLocal(this.lastLocalData));
    }

    loadLocal(overrides = {}) {
        const local = {},
            { project = {}, annotations = [], ...parsed } = parseOverrides(overrides);

        if (project.videoSource) {
            local.media = [{
                url: project.videoSource,
                /* don't provide poster image if not starting at the beginning */
                thumbnail: this.options.startTime > 0 ? undefined : project.thumbnail,
                updateResolution: minimumResolution(
                    annotations
                        .filter(a => (
                                a.target === ANNOTATION_TARGETS.PLAYER &&
                                a.animation &&
                                a.animation.resolution
                            )
                        )
                        .map(a => a.animation)
                )
            }];

            project.playerType = WRAPPER_TYPES.VIDEO_JS;
        } else {
            project.playerType = WRAPPER_TYPES.NO_MEDIA;
        }

        parsed.analytics = parsed.analytics || {};

        if (this.options.editor || this.options.disableAnalytics) {
            parsed.analytics.disableAnalytics = true;
        }

        return { ...local, ...parsed, project, annotations, state: this.stateEngine.activeState };
    }

    reconcileSetup(overrides = {}) {
        let reconciled = {
            ...overrides,
            annotations: this.addRuntimeAnnotations(overrides.annotations)
        };

        this.stateEngine.configuration = reconciled;
        reconciled = this.stateEngine.statefulConfiguration;

        reconciled.annotations = distillAndApplyMetadata(reconciled.annotations);

        requestIdleCallback(() => {
            getStateLogger().addProject(reconciled.media);
            getStateLogger().setAnnotations(
                reconciled.annotations
                    .filter(a => !this.runtimeAnnotations.some(ra => ra.id === a.id)) // exclude runtime annotations
            );
        })

        return reconciled;
    }

    addRuntimeAnnotations(overrides = []) {
        return (
            overrides
                .concat(this.runtimeAnnotations)
        );
    }

    updateRuntimeAnnotationModifications(id, newModifications = {}) {
        const existingMods = this.runtimeAnnotationModifications[id];

        if (!existingMods) {
            this.runtimeAnnotationModifications[id] = { ...newModifications };
        } else {
            this.runtimeAnnotationModifications[id] = mergeAnnotations(this.runtimeAnnotationModifications[id], newModifications);
        }
    }

    // runtime annotations modifications may need to be re-applied
    // if overrides are updated externally (see `reload`)
    applyRuntimeAnnotationModifications(annotations = []) {
        return (
            annotations.map(a => (
                    this.runtimeAnnotationModifications[a.id] ?
                        mergeAnnotations(a, this.runtimeAnnotationModifications[a.id]) :
                        a
                )
            )
        );
    }

    checkVersion(version) {
        if (supportedVersion(version) === VERSION_SUPPORTED.YES) {
            return true;
        } else if (supportedVersion(version) === VERSION_SUPPORTED.MAYBE) {
            console.warn('Older config detected. Setup issues may occur');
            return true;
        }

        return false;
    }
}